import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { find, truncate, filter, capitalize, take } from 'lodash';
import {
  MenuItem, Dropdown, Tooltip, OverlayTrigger,
  FormGroup, ControlLabel, HelpBlock,
} from 'react-bootstrap';
import Fa from 'react-fontawesome';
import styles from './styles.css';
import ProductDropdown from './InitativeProductDropdown';
import ProgramType from '../../../../components/program-type';

class InitiativeSelector extends Component {
  constructor() {
    super();
    this.state = {
      touched: false,
    };
    this.selectProgram = this.selectProgram.bind(this);
    this.selectProduct = this.selectProduct.bind(this);
    this.selectCollector = this.selectCollector.bind(this);
  }

  programHasDefaultCollector(program) {
    return program && program.defaultCollector !== null && program.defaultCollector !== undefined;
  }

  programHasProducts(program) {
    return program && program.productList && program.productList.length > 0;
  }


  selectProgram(program) {
    this.props.onProgramSelect(program);
    this.setState({ touched: true });
  }

  selectProduct(product) {
    this.props.onProductSelect(product);
    this.setState({ touched: true });
  }
  
  selectCollector(collector) {
    this.props.onCollectorSelect(collector);
    this.setState({ touched: true });
  }

  renderCollectorInfo(collector) {
    const name = collector.collector_name;
    return (
      <span>{name}</span>
    );
  }

  renderProductDropdown() {
    const { touched } = this.state;
    const { programs, requireProducts, allowMultipleProducts } = this.props;
    const selectedProgram = find(programs, { selected: true });
    if (!selectedProgram) {
      return null;
    }
    if (selectedProgram.hasAtLeastOneMappedProduct) {
      // we submit all mapped products, ignore the rest
      return null;
    }
    const products = selectedProgram.productList || [];
    const programHasProducts = this.programHasProducts(selectedProgram);
    let selectedProducts = filter(products, { selected: true });
    if (!allowMultipleProducts) {
      selectedProducts = take(selectedProducts, 1);
    }
    // const missingProduct = (touched && selectedProducts.length === 0);
    const missingProduct = (selectedProducts.length === 0);
    const hasError = requireProducts && (!programHasProducts || missingProduct);
    const tip = (<Tooltip id="program-tip">This TruVoice Program does not has any product setup.</Tooltip>);
    return (
      <FormGroup className={hasError ? 'has-error' : null}>
        <ControlLabel>Primary Product{allowMultipleProducts ? '(s)' : ''} Considered:{requireProducts ? <sup className="text-danger">*</sup> : ''}:</ControlLabel>
        <div>
          {products.length > 0 &&
            <ProductDropdown
              products={products}
              onProductSelect={this.selectProduct}
              allowMultipleProducts={allowMultipleProducts}
            />
          }
        </div>
        {hasError &&
          <HelpBlock>
            {products.length === 0 &&
              <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={tip}>
                <span><Fa name="exclamation-triangle" /> Missing products</span>
              </OverlayTrigger>
            }
            {products.length > 0 && missingProduct && <div>Please select a product</div>}
          </HelpBlock>
        }
      </FormGroup>
    );
  }

  renderProgramDropdown() {
    const { programs } = this.props;
    const selectedProgram = find(programs, { selected: true });
    const { touched } = this.state;
    const hasError = touched && selectedProgram === null;
    if (selectedProgram && programs.length === 1) {
      return null;
    }
    return (
      <FormGroup className={hasError ? 'has-error' : null}>
        <ControlLabel>TruVoice Program</ControlLabel>
        <div>
          <Dropdown
            id="programs"
            onSelect={this.selectProgram}
          >
            <Dropdown.Toggle>
              {selectedProgram ? <span><ProgramType type={selectedProgram.type_name} /> {truncate(selectedProgram.name, { length: 20 })}</span> : 'Select a program'}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {programs.map((program) => {
                const active = selectedProgram && selectedProgram.id === program.id;
                return (
                  <MenuItem
                    eventKey={program}
                    active={active}
                    key={program.id}
                  >
                    <ProgramType type={program.type_name} /> {program.name}
                  </MenuItem>
                );
              })}
            </Dropdown.Menu>
          </Dropdown>
        </div>
        {hasError && <HelpBlock>Please select a TruVoice Program</HelpBlock>}
      </FormGroup>
    );
  }

  renderCollectorDropdown() {
    const { programs, withPreferCollector } = this.props;
    const selectedProgram = find(programs, { selected: true });
    const programHasDefaultCollector = this.programHasDefaultCollector(selectedProgram);
    const selectedCollector = selectedProgram ? selectedProgram.defaultCollector : null;


    const hasError = !programHasDefaultCollector;
    const tip = (<Tooltip id="collector-tip">This TruVoice Program does not has a default collector setup.</Tooltip>);
    if (!selectedProgram || (selectedProgram && programHasDefaultCollector && selectedProgram.collectors.length === 1)) {
      return null;
    }

    if (selectedCollector && withPreferCollector && withPreferCollector === selectedCollector.collector_id) {
      return null;
    }

    return (
      <FormGroup className={hasError ? 'has-error' : null}>
        <ControlLabel>Collector</ControlLabel>
        {selectedProgram.collectors.length > 0 &&
          <div>
            <Dropdown
              id="collector"
              onSelect={this.selectCollector}
            >
              <Dropdown.Toggle>
                {selectedCollector ? truncate(selectedCollector.collector_name, { length: 20 }) : 'Select a collector'}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {selectedProgram.collectors.map((collector) => {
                  const active = selectedCollector && selectedCollector.collector_id === collector.collector_id && selectedCollector.type === collector.type;
                  return (
                    <MenuItem
                      active={active}
                      eventKey={collector}
                      key={`${collector.collector_id}-${collector.type}`}
                    >
                      {collector.collector_name} <span className="badge">{capitalize(collector.type)}</span>
                    </MenuItem>
                  );
                })}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        }

        {hasError &&
          <HelpBlock>
            {!programHasDefaultCollector &&
              <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={tip}>
                <span><Fa name="exclamation-triangle" /> Missing default collector</span>
              </OverlayTrigger>
            }
          </HelpBlock>
        }
      </FormGroup>
    );
  }

  render() {
    return (
      <div className={styles.steps}>
        {this.renderProgramDropdown()}
        {this.renderCollectorDropdown()}
        {this.renderProductDropdown()}
        {this.props.children}
      </div>
    );
  }
}


InitiativeSelector.propTypes = {
  onProgramSelect: PropTypes.func,
  onProductSelect: PropTypes.func,
  onCollectorSelect: PropTypes.func,
  withPreferCollector: PropTypes.string,
  programs: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    hasAtLeastOneMappedProduct: PropTypes.bool,
    productList: PropTypes.arrayOf(PropTypes.shape({})),
  })),
  children: PropTypes.node,
  requireProducts: PropTypes.bool,
  allowMultipleProducts: PropTypes.bool,
};
InitiativeSelector.defaultProps = {
  onProgramSelect: () => {},
  onProductSelect: () => {},
  onCollectorSelect: () => {},
  requireProducts: true,
  programs: [],
  children: null,
  withPreferCollector: null,
  allowMultipleProducts: true,
};

export default InitiativeSelector;
